/**
 * Orbit description, secular perturbation rates and special orbit solvers.
 *
 * This module turns raw state vectors into the numbers a mission planner reads —
 * apoapsis, periapsis, period, ground track — and solves the inverse problems
 * behind the standard orbit types the Orbit Editor offers.
 *
 * The secular rates come from first-order J2 theory, which is the level of
 * fidelity that actually explains orbit design decisions: why sun-synchronous
 * orbits are retrograde, why Molniya orbits sit at 63.4 degrees, and why GEO
 * needs station-keeping.
 */

import { EARTH, TAU, type CelestialBodyParams } from './constants.js';
import { altitudeAbove, ecefToGeodetic, eciToEcef, flightPathAngle, type Geodetic } from './frames.js';
import {
  elementsToState,
  meanMotion,
  orbitalPeriod,
  propagateKepler,
  specificEnergy,
  stateToElements,
  visViva,
  type OrbitalElements,
  type StateVector,
} from './kepler.js';
import { gmst } from './time.js';
import { clampUnit, wrapTwoPi } from './units.js';
import { cross, norm, type ReadonlyVec3 } from './vec3.js';

/** Mean motion of the Earth about the Sun, rad/s — the sun-synchronous target rate. */
export const EARTH_ORBIT_MEAN_MOTION = TAU / (365.2421897 * 86400);

export interface OrbitSummary {
  elements: OrbitalElements;
  /** Apoapsis radius from the body centre, m. Infinite for open orbits. */
  apoapsisRadius: number;
  /** Periapsis radius from the body centre, m. */
  periapsisRadius: number;
  /** Apoapsis altitude above the equatorial radius, m. */
  apoapsisAltitude: number;
  /** Periapsis altitude above the equatorial radius, m. */
  periapsisAltitude: number;
  /** Orbital period, s. Infinite for open orbits. */
  period: number;
  /** Current speed, m/s. */
  speed: number;
  /** Speed at apoapsis, m/s. */
  apoapsisSpeed: number;
  /** Speed at periapsis, m/s. */
  periapsisSpeed: number;
  /** Current radius, m. */
  radius: number;
  /** Current altitude above the ellipsoid, m. */
  altitude: number;
  /** Specific orbital energy, J/kg. */
  energy: number;
  /** Angular momentum magnitude, m^2/s. */
  angularMomentum: number;
  /** Mean motion, rad/s. */
  meanMotion: number;
  /** Revolutions per solar day. */
  revsPerDay: number;
  /** Flight path angle, rad. */
  flightPathAngle: number;
  /** True when the orbit's periapsis is below the atmosphere and it will decay. */
  willDecay: boolean;
  /** True when the trajectory intersects the body. */
  impacts: boolean;
  /** Conic classification. */
  type: 'circular' | 'elliptical' | 'parabolic' | 'hyperbolic';
}

/** Full derived description of an orbit from a state vector. */
export function describeOrbit(
  position: ReadonlyVec3,
  velocity: ReadonlyVec3,
  body: CelestialBodyParams = EARTH,
): OrbitSummary {
  const mu = body.mu;
  const elements = stateToElements(position, velocity, mu);
  const { semiMajorAxis: a, eccentricity: e } = elements;
  const radius = norm(position);
  const speed = norm(velocity);

  const closed = e < 1 && Number.isFinite(a);
  const periapsisRadius = Number.isFinite(a) ? a * (1 - e) : 0;
  const apoapsisRadius = closed ? a * (1 + e) : Number.POSITIVE_INFINITY;

  const type: OrbitSummary['type'] =
    e < 1e-6 ? 'circular' : e < 1 - 1e-9 ? 'elliptical' : e < 1 + 1e-9 ? 'parabolic' : 'hyperbolic';

  return {
    elements,
    apoapsisRadius,
    periapsisRadius,
    apoapsisAltitude: closed ? apoapsisRadius - body.equatorialRadius : Number.POSITIVE_INFINITY,
    periapsisAltitude: periapsisRadius - body.equatorialRadius,
    period: orbitalPeriod(a, mu),
    speed,
    apoapsisSpeed: closed ? visViva(apoapsisRadius, a, mu) : 0,
    periapsisSpeed: periapsisRadius > 0 ? visViva(periapsisRadius, a, mu) : 0,
    radius,
    altitude: altitudeAbove(position, body),
    energy: specificEnergy(speed, radius, mu),
    angularMomentum: norm(cross(position, velocity)),
    meanMotion: closed ? meanMotion(a, mu) : 0,
    revsPerDay: closed ? 86400 / orbitalPeriod(a, mu) : 0,
    flightPathAngle: flightPathAngle(position, velocity),
    // A periapsis inside the sensible atmosphere means the orbit is not stable.
    willDecay: closed && periapsisRadius - body.equatorialRadius < 120_000,
    impacts: periapsisRadius <= body.equatorialRadius,
    type,
  };
}

// --- Secular J2 rates --------------------------------------------------------

/**
 * Secular drift rate of the right ascension of the ascending node, rad/s.
 *
 * Negative (westward) for prograde orbits, positive for retrograde ones. This is
 * the single most useful perturbation in orbit design: it is what makes
 * sun-synchronous orbits possible, and what forces constellation planes apart
 * over time.
 */
export function nodalRegressionRate(
  semiMajorAxis: number,
  eccentricity: number,
  inclination: number,
  body: CelestialBodyParams = EARTH,
): number {
  const p = semiMajorAxis * (1 - eccentricity * eccentricity);
  if (p <= 0) return 0;
  const n = meanMotion(semiMajorAxis, body.mu);
  const ratio = body.equatorialRadius / p;
  return -1.5 * n * body.J2 * ratio * ratio * Math.cos(inclination);
}

/**
 * Secular drift rate of the argument of periapsis, rad/s.
 * Vanishes at the critical inclination of 63.435 degrees, which is exactly why
 * Molniya and Tundra orbits use it — their apogee stays over the same hemisphere.
 */
export function apsidalPrecessionRate(
  semiMajorAxis: number,
  eccentricity: number,
  inclination: number,
  body: CelestialBodyParams = EARTH,
): number {
  const p = semiMajorAxis * (1 - eccentricity * eccentricity);
  if (p <= 0) return 0;
  const n = meanMotion(semiMajorAxis, body.mu);
  const ratio = body.equatorialRadius / p;
  const cosI = Math.cos(inclination);
  return 0.75 * n * body.J2 * ratio * ratio * (5 * cosI * cosI - 1);
}

/** The critical inclination at which apsidal precession vanishes, rad (63.435 deg). */
export const CRITICAL_INCLINATION = Math.acos(Math.sqrt(1 / 5));

/**
 * Inclination that makes an orbit sun-synchronous, rad, or `null` when no
 * solution exists (which happens for orbits too high or too eccentric for J2 to
 * supply the required precession).
 *
 * Solves `nodalRegressionRate(a, e, i) = EARTH_ORBIT_MEAN_MOTION` for i. The
 * answer is always retrograde, since a prograde node regresses the wrong way.
 */
export function sunSynchronousInclination(
  semiMajorAxis: number,
  eccentricity = 0,
  body: CelestialBodyParams = EARTH,
): number | null {
  const p = semiMajorAxis * (1 - eccentricity * eccentricity);
  if (p <= 0) return null;
  const n = meanMotion(semiMajorAxis, body.mu);
  const ratio = body.equatorialRadius / p;
  const denominator = 1.5 * n * body.J2 * ratio * ratio;
  if (denominator === 0) return null;
  const cosI = -EARTH_ORBIT_MEAN_MOTION / denominator;
  if (cosI < -1 || cosI > 1) return null;
  return Math.acos(cosI);
}

/**
 * Semi-major axis of a repeat ground-track orbit that completes
 * `revolutions` orbits in `days` nodal days, m. Solved by fixed-point iteration
 * because the required nodal period itself depends on J2, which depends on the
 * semi-major axis.
 */
export function repeatGroundTrackSemiMajorAxis(
  revolutions: number,
  days: number,
  inclination: number,
  eccentricity = 0,
  body: CelestialBodyParams = EARTH,
): number {
  // Start from the unperturbed Keplerian solution.
  const targetPeriod = (days * 86164.0905) / revolutions;
  let a = Math.cbrt((body.mu * targetPeriod * targetPeriod) / (TAU * TAU));

  for (let i = 0; i < 40; i++) {
    const nodeRate = nodalRegressionRate(a, eccentricity, inclination, body);
    // The ground track repeats against the rotating Earth, so the effective
    // rotation rate is reduced by the node's own drift.
    const effectiveRotation = body.rotationRate - nodeRate;
    const requiredPeriod = (days * (TAU / effectiveRotation)) / revolutions;
    const next = Math.cbrt((body.mu * requiredPeriod * requiredPeriod) / (TAU * TAU));
    if (Math.abs(next - a) < 1e-4) return next;
    a = next;
  }
  return a;
}

// --- Special orbit builders --------------------------------------------------

/** Circular orbit elements at a given altitude and inclination. */
export function circularOrbit(
  altitude: number,
  inclination: number,
  raan = 0,
  trueAnomaly = 0,
  body: CelestialBodyParams = EARTH,
): OrbitalElements {
  return {
    semiMajorAxis: body.equatorialRadius + altitude,
    eccentricity: 0,
    inclination,
    raan,
    argumentOfPeriapsis: 0,
    trueAnomaly,
  };
}

/** Elements from a pair of apsis altitudes. */
export function orbitFromApsides(
  periapsisAltitude: number,
  apoapsisAltitude: number,
  inclination: number,
  raan = 0,
  argumentOfPeriapsis = 0,
  trueAnomaly = 0,
  body: CelestialBodyParams = EARTH,
): OrbitalElements {
  const rp = body.equatorialRadius + Math.min(periapsisAltitude, apoapsisAltitude);
  const ra = body.equatorialRadius + Math.max(periapsisAltitude, apoapsisAltitude);
  const a = (rp + ra) / 2;
  return {
    semiMajorAxis: a,
    eccentricity: (ra - rp) / (ra + rp),
    inclination,
    raan,
    argumentOfPeriapsis,
    trueAnomaly,
  };
}

/** Geostationary elements: circular, equatorial, synchronous with the rotation rate. */
export function geostationaryOrbit(longitude = 0, body: CelestialBodyParams = EARTH): OrbitalElements {
  const a = Math.cbrt(body.mu / (body.rotationRate * body.rotationRate));
  return {
    semiMajorAxis: a,
    eccentricity: 0,
    inclination: 0,
    raan: 0,
    argumentOfPeriapsis: 0,
    // For an equatorial circular orbit the true anomaly is the true longitude,
    // so placing the satellite over a given longitude is a sidereal-time offset.
    trueAnomaly: wrapTwoPi(longitude),
  };
}

/** Sun-synchronous circular orbit at a given altitude, or `null` if unreachable. */
export function sunSynchronousOrbit(
  altitude: number,
  localTimeOfAscendingNodeHours = 10.5,
  body: CelestialBodyParams = EARTH,
): OrbitalElements | null {
  const a = body.equatorialRadius + altitude;
  const inclination = sunSynchronousInclination(a, 0, body);
  if (inclination === null) return null;
  // The local mean solar time of the node maps directly onto the RAAN offset
  // from the Sun's right ascension; 24 h spans a full turn.
  const raan = wrapTwoPi(((localTimeOfAscendingNodeHours - 12) / 24) * TAU);
  return {
    semiMajorAxis: a,
    eccentricity: 0,
    inclination,
    raan,
    argumentOfPeriapsis: 0,
    trueAnomaly: 0,
  };
}

/**
 * Molniya-type orbit: highly eccentric, 12-hour period, at the critical
 * inclination so apogee dwell stays over the chosen hemisphere.
 */
export function molniyaOrbit(
  periapsisAltitude = 500_000,
  body: CelestialBodyParams = EARTH,
): OrbitalElements {
  const period = 86164.0905 / 2;
  const a = Math.cbrt((body.mu * period * period) / (TAU * TAU));
  const rp = body.equatorialRadius + periapsisAltitude;
  return {
    semiMajorAxis: a,
    eccentricity: 1 - rp / a,
    inclination: CRITICAL_INCLINATION,
    raan: 0,
    // Apogee over the northern hemisphere.
    argumentOfPeriapsis: (270 * Math.PI) / 180,
    trueAnomaly: 0,
  };
}

// --- Ground track and coverage ----------------------------------------------

export interface GroundTrackPoint {
  /** Seconds since J2000.0. */
  t: number;
  latitude: number;
  longitude: number;
  altitude: number;
}

/**
 * Sample the ground track of an orbit.
 *
 * Propagation is two-body plus the secular J2 node drift, applied as a rotation
 * of the orbital plane. That is a deliberate trade: a full numerical propagation
 * would be more accurate but far too slow to redraw interactively as the user
 * drags an element slider, and the node drift is the term that visibly matters
 * over the handful of orbits shown at once.
 */
export function groundTrack(
  elements: OrbitalElements,
  epochSeconds: number,
  durationSeconds: number,
  sampleCount = 360,
  body: CelestialBodyParams = EARTH,
): GroundTrackPoint[] {
  const points: GroundTrackPoint[] = [];
  const nodeRate = nodalRegressionRate(elements.semiMajorAxis, elements.eccentricity, elements.inclination, body);
  const argpRate = apsidalPrecessionRate(elements.semiMajorAxis, elements.eccentricity, elements.inclination, body);

  const base = elementsToState(elements, body.mu);

  for (let i = 0; i < sampleCount; i++) {
    const dt = (durationSeconds * i) / (sampleCount - 1);
    const drifted: OrbitalElements = {
      ...elements,
      raan: wrapTwoPi(elements.raan + nodeRate * dt),
      argumentOfPeriapsis: wrapTwoPi(elements.argumentOfPeriapsis + argpRate * dt),
    };
    // Propagate the in-plane motion analytically, then re-orient with the drifted
    // node and periapsis angles.
    const inPlane = propagateKepler(base.position, base.velocity, dt, body.mu);
    const nu = stateToElements(inPlane.position, inPlane.velocity, body.mu).trueAnomaly;
    const state = elementsToState({ ...drifted, trueAnomaly: nu }, body.mu);

    const t = epochSeconds + dt;
    const geodetic = ecefToGeodetic(eciToEcef(state.position, gmst(t)), body);
    points.push({
      t,
      latitude: geodetic.latitude,
      longitude: geodetic.longitude,
      altitude: geodetic.altitude,
    });
  }
  return points;
}

/** Ground track from an explicit list of inertial states, for a simulated flight. */
export function groundTrackFromStates(
  samples: readonly { t: number; position: ReadonlyVec3 }[],
  body: CelestialBodyParams = EARTH,
): GroundTrackPoint[] {
  return samples.map(({ t, position }) => {
    const g = ecefToGeodetic(eciToEcef(position, gmst(t)), body);
    return { t, latitude: g.latitude, longitude: g.longitude, altitude: g.altitude };
  });
}

/**
 * Earth-central half-angle visible from an altitude, given a minimum elevation
 * angle at the ground station, rad. This is the coverage footprint radius.
 */
export function horizonHalfAngle(
  altitude: number,
  minimumElevation = 0,
  body: CelestialBodyParams = EARTH,
): number {
  const re = body.equatorialRadius;
  const ratio = re / (re + altitude);
  const inner = ratio * Math.cos(minimumElevation);
  return Math.acos(clampUnit(inner)) - minimumElevation;
}

/** Ground swath radius on the surface, m. */
export const footprintRadius = (
  altitude: number,
  minimumElevation = 0,
  body: CelestialBodyParams = EARTH,
): number => horizonHalfAngle(altitude, minimumElevation, body) * body.equatorialRadius;

/** Fraction of the sphere covered by one satellite's footprint, dimensionless. */
export function coverageFraction(
  altitude: number,
  minimumElevation = 0,
  body: CelestialBodyParams = EARTH,
): number {
  const lambda = horizonHalfAngle(altitude, minimumElevation, body);
  // Spherical cap area / sphere area.
  return (1 - Math.cos(lambda)) / 2;
}

/**
 * Maximum latitude reached by an orbit, rad. Equals the inclination for prograde
 * orbits and 180 degrees minus it for retrograde ones — which is why a
 * sun-synchronous orbit at 98.6 degrees still only reaches 81.4 degrees of
 * latitude, a fact that surprises people planning polar coverage.
 */
export const maximumLatitude = (inclination: number): number =>
  inclination <= Math.PI / 2 ? inclination : Math.PI - inclination;

/**
 * Estimated orbital lifetime from atmospheric drag, s.
 *
 * A deliberately coarse model: it assumes a circular orbit decaying quasi-statically
 * with a constant ballistic coefficient and an exponential atmosphere at the
 * current altitude. Good for an order-of-magnitude figure in the analytics panel
 * — anything more requires a density history nobody has in advance.
 */
export function estimatedOrbitalLifetime(
  altitude: number,
  ballisticCoefficient: number,
  densityFn: (altitude: number) => number,
  body: CelestialBodyParams = EARTH,
): number {
  if (altitude <= 0) return 0;
  let h = altitude;
  let elapsed = 0;
  const dt = 3600;
  const maxYears = 500;
  const limit = maxYears * 365.25 * 86400;

  while (h > 100_000 && elapsed < limit) {
    const r = body.equatorialRadius + h;
    const rho = densityFn(h);
    if (rho <= 0) {
      // Nothing to decay through at this altitude on this model.
      return Number.POSITIVE_INFINITY;
    }
    const v = Math.sqrt(body.mu / r);
    // da/dt for a circular orbit under drag: -rho * v * a / B, with B = m/(Cd*A).
    const dadt = (-rho * v * r) / ballisticCoefficient;
    h += dadt * dt;
    elapsed += dt;
  }
  return elapsed;
}

/** Convert a state vector to a summary plus its geodetic sub-point. */
export function orbitStatus(
  state: StateVector,
  epochSeconds: number,
  body: CelestialBodyParams = EARTH,
): { summary: OrbitSummary; subPoint: Geodetic } {
  return {
    summary: describeOrbit(state.position, state.velocity, body),
    subPoint: ecefToGeodetic(eciToEcef(state.position, gmst(epochSeconds)), body),
  };
}
